<template>
  <div class="install-success">
    <div class="success-content">
      <!-- Success Header -->
      <div class="success-header">
        <div class="success-icon">🎉</div>
        <h1>Congratulations! Prec8 is now installed!</h1>
        <p class="success-subtitle">
          Your team is ready to start recognizing each other and building a culture of appreciation.
        </p>
      </div>

      <!-- Next Steps -->
      <div class="next-steps">
        <h2>What would you like to do next?</h2>
        
        <div class="options-grid">
          <!-- Dashboard Option -->
          <div class="option-card dashboard-option">
            <div class="option-icon">📊</div>
            <h3>Go to Dashboard</h3>
            <p>Set up your team's appreciation emoji, configure settings, and start managing your workspace.</p>
            <button @click="goToDashboard" class="option-button dashboard-button">
              <span>Open Dashboard</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <!-- Slack Option -->
          <div class="option-card slack-option">
            <div class="option-icon">💬</div>
            <h3>Go to Slack</h3>
            <p>Jump back to Slack to start giving recognition to your teammates right away.</p>
            <a :href="launchSlackUrl" class="option-button slack-button">
              <svg width="20" height="20" viewBox="0 0 122.8 122.8" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#e01e5a"/>
                <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36c5f0"/>
                <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2eb67d"/>
                <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ecb22e"/>
              </svg>
              <span>Open Slack</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Quick Tips -->
      <div class="quick-tips">
        <h3>💡 Quick Tips</h3>
        <ul>
          <li>Use your team's custom emoji to give recognition in any Slack channel</li>
          <li>Check the leaderboard to see who's getting the most appreciation</li>
          <li>Redeem points for rewards in the rewards store</li>
        </ul>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import Footer from './components/Footer.vue'

@Options({
  components: {
    Footer
  },
})
export default class InstallSuccess extends Vue {
  launchSlackUrl = "";

  mounted() {
    const teamId = this.$route.query.teamId;
    this.launchSlackUrl =
      "https://slack.com/app_redirect?app=A0777EW17AN&tab=about&team=" + teamId;
  }

  goToDashboard() {
    console.log('Navigating to dashboard...');
    console.log('Current route:', this.$route.path);
    console.log('Router instance:', this.$router);
    
    try {
      // Try the standard router navigation first
      this.$router.push('/dashboard');
      console.log('Router navigation successful');
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
      
      // Fallback 1: Try using replace instead of push
      try {
        this.$router.replace('/dashboard');
        console.log('Router replace successful');
      } catch (replaceError) {
        console.error('Router replace also failed:', replaceError);
        
        // Fallback 2: Try using window.location
        console.log('Trying window.location fallback...');
        window.location.hash = '#/dashboard';
      }
    }
  }
}
</script>

<style lang="scss">
.install-success {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
  line-height: 1.5;
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.success-content {
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
}

.success-header {
  margin-bottom: 3rem;
  
  .success-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    animation: bounce 1s ease-in-out;
  }
  
  h1 {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    color: #1a1a1a;
  }
  
  .success-subtitle {
    font-size: 1.25rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
}

.next-steps {
  margin-bottom: 3rem;
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 2rem;
    color: #1a1a1a;
  }
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.option-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e1e5e9;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
  
  .option-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
  }
  
  p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    font-size: 1rem;
  }
}

.option-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: 100%;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
  }
}

.dashboard-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  
  &:hover {
    background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
}

.slack-button {
  background: white;
  color: #1a1a1a;
  border: 2px solid #e1e5e9;
  
  &:hover {
    border-color: #1a1a1a;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.quick-tips {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e1e5e9;
  text-align: left;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    text-align: center;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 0.5rem 0;
      color: #666;
      position: relative;
      padding-left: 1.5rem;
      
      &:before {
        content: "•";
        color: #667eea;
        font-weight: bold;
        position: absolute;
        left: 0;
      }
    }
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@media (max-width: 768px) {
  .success-content {
    padding: 1rem;
  }
  
  .success-header {
    h1 {
      font-size: 2rem;
    }
    
    .success-subtitle {
      font-size: 1.1rem;
    }
  }
  
  .options-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .option-card {
    padding: 1.5rem;
  }
  
  .quick-tips {
    padding: 1.5rem;
  }
}
</style>
